import { useMemo } from 'react';

import { graphql } from '@/__generated__';
import {
  ProjectRecommendationSort,
  RuleFilter,
  RuleNameEnum,
} from '@/__generated__/graphql';
import { useCacheMonitor } from '@/context/DiscoveryUrqlProvider';
import { usePaginatedQuery } from '@/reducers/usePaginatedQuery';
import { ParsedUniqueId, parseUniqueId } from '@/typings/parseUniqueId';
import { truthy } from '@/util/array';

export const GetRecommendations = graphql(/* GraphQL */ `
  query Recommendations(
    $environmentId: BigInt!
    $first: Int!
    $filter: RuleFilter
    $after: String
    $sort: ProjectRecommendationSort
  ) {
    recommendations(environmentId: $environmentId) {
      lastUpdatedAt
      rules(first: $first, filter: $filter, after: $after, sort: $sort) {
        totalCount
        pageInfo {
          endCursor
          hasNextPage
        }
        edges {
          cursor
          node {
            name
            uniqueId
            violationId
            severity
            category
          }
        }
      }
    }
  }
`);

type RecommendationsParams = {
  first: number;
  after?: string;
  environmentId: number | undefined;
  filter: Partial<RuleFilter>;
  sort?: Partial<ProjectRecommendationSort>;
  pause?: boolean;
};

const RecommendationDescriptions: Record<
  RuleNameEnum,
  undefined | ((parsed_unique_id: ParsedUniqueId) => string)
> = {
  UNDOCUMENTED_MODEL: (parsed_unique_id: ParsedUniqueId) => {
    return `\`${parsed_unique_id.remainder}\` is missing a description. Add a clear description to give context to this ${parsed_unique_id.resourceType}'s purpose`;
  },
  SOURCE_FANOUT: (parsed_unique_id: ParsedUniqueId) => {
    return `\`${parsed_unique_id.remainder}\` is the direct parent of multiple resources in the DAG. Each source node should be referenced by a single model that performs basic operations, such as renaming, recasting, and other light transformations to maintain consistency through out the project.`;
  },
  DUPLICATE_SOURCES: (parsed_unique_id: ParsedUniqueId) => {
    return `\`${parsed_unique_id.remainder}\` is a duplicate source. dbt Resources should not point to the same warehouse object!`;
  },
  PUBLIC_MODEL_MISSING_CONTRACT: (parsed_unique_id: ParsedUniqueId) => {
    return `\`${parsed_unique_id.remainder}\` is a public model, but does not have a [model contract](https://docs.getdbt.com/docs/collaborate/govern/model-contracts) defined. Adding a model contract ensures consistent table shape and datatypes to downstream consumers of this model.`;
  },
  DIRECT_JOIN_TO_SOURCE: (parsed_unique_id: ParsedUniqueId) => {
    return `\`${parsed_unique_id.remainder}\` references both a model and a source node. We highly recommend having a one-to-one relationship between sources and their corresponding staging model, and not having any other model reading from the source. Those staging models are then the ones read from by the other downstream models. This allows renaming your columns and doing minor transformation on your source data only once and being consistent across all the models that will consume the source data.`;
  },
  ROOT_MODEL: (parsed_unique_id: ParsedUniqueId) => {
    return `\`${parsed_unique_id.remainder}\` is a root model`;
  },
  MULTIPLE_SOURCES_JOINED: (parsed_unique_id: ParsedUniqueId) => {
    return `\`${parsed_unique_id.remainder}\` joins multiple sources. We very strongly believe that a staging model is the atomic unit of data modeling. Each staging model bears a one-to-one relationship with the source data table it represents. With that in mind, two {{ source() }} declarations in one staging model likely means we are not being composable enough and there are individual building blocks which could be broken out into their respective models`;
  },
  MISSING_PRIMARY_KEY_TEST: (parsed_unique_id: ParsedUniqueId) => {
    return `\`${parsed_unique_id.remainder}\` is missing a primary key test. This test is important to ensure that the model is not generating duplicate records. Models without proper tests on their grain are a risk to the reliability and scalability of your project.`;
  },
  EXPOSURE_DEPENDENT_ON_VIEW: (parsed_unique_id: ParsedUniqueId) => {
    return `\`${parsed_unique_id.remainder}\` is an exposure with at least one view/ephemeral model parent. Models that are referenced by an exposure are likely to be used heavily in downstream systems, and therefore need to be performant when queried. Consider using a table or incremental materialization`;
  },
  UNDOCUMENTED_SOURCE_TABLE: (parsed_unique_id: ParsedUniqueId) => {
    return `\`${parsed_unique_id.remainder}\` is missing a table-level description. Good documentation for your dbt sources will help downstream consumers discover the data that dbt builds off of`;
  },
  UNUSED_SOURCE_TABLE: (parsed_unique_id: ParsedUniqueId) => {
    return `\`${parsed_unique_id.remainder}\` is not used. This represents either a source that you have defined in YML but never brought into a model or a model that was deprecated and the corresponding rows in the source block of the YML file were not deleted at the same time. This simply represents the buildup of cruft in the project that doesn’t need to be there.`;
  },
};

export const useRecommendations = ({
  first,
  after,
  environmentId,
  filter: targetFilters,
  sort,
  pause,
}: RecommendationsParams) => {
  const filter: Partial<RuleFilter> = {
    ...targetFilters,
    ruleNames: targetFilters.ruleNames?.length
      ? targetFilters.ruleNames
      : Object.values(RuleNameEnum),
  };
  const query = usePaginatedQuery(
    {
      query: GetRecommendations,
      variables: {
        first,
        after,
        environmentId,
        filter,
        sort,
      },
      pause: pause || !environmentId || !first,
    },
    (data) => {
      const rules = data.recommendations.rules;
      const pageInfo = rules.pageInfo;
      if (rules == null || pageInfo == null) return undefined;
      return {
        totalCount: rules.totalCount ?? null,
        endCursor: pageInfo.endCursor ?? null,
        hasNextPage: pageInfo.hasNextPage,
      };
    },
    (data) => {
      return (data.recommendations.rules.edges || [])
        .map((edge) => edge.node)
        .filter(truthy);
    },
    (data) => data.recommendations.lastUpdatedAt,
  );

  useCacheMonitor({ applied: query.lastUpdatedAt });
  return useMemo(() => {
    // Populate descriptions
    const data = query.data?.map((node) => {
      return {
        ...node,
        description: RecommendationDescriptions[node.name as RuleNameEnum]?.(
          parseUniqueId(node.uniqueId),
        ),
      };
    });
    return { ...query, data };
  }, [query]);
};
